// Invite keys (INVITE-LINK-PASSKEY-FIRST.md §1.2, 2026-10-08): the per-(owner,
// recipient) member key an owner mints at invite time. Once claimed it lives
// in the RECIPIENT's identity bundle as `inviteKeys[]`, public halves in the
// clear and the secret halves sealed to the recipient's own identity
// encryption key — so any browser that holds the identity can open it, and
// adding one never re-wraps the existing wraps (a passkey wrap cannot be
// re-wrapped without a PRF touch, the backup-code wrap not without the code).
//
// v3 (XWING.md §3.3–§3.4): the key's encryption half is X-Wing (`encPk` 1216
// bytes, `encSk` the 32-byte seed) and the secrets travel in a v3 sealed box
// under the context {ownerUserId, memberUserId} — the same box that carries
// epoch seeds and the device-transfer bundle.
//
// Pure helpers, no API or store imports: shared by the signup bootstrap, the
// rotation, the decryptor and the invite flow without import cycles.

import { b64, ub64, sealBox, openBox, epochPkFromSeed } from './shared/envelope.js'

/** The sealed-box context of an invite key (XWING.md §3.3). */
const inviteKeyContext = (k) => ({ ownerUserId: k.ownerUserId, memberUserId: k.memberUserId })

/**
 * @param inviteKey { ownerUserId, memberUserId, encPk, signPk, encSk, signSk } (all base64)
 * @param idEncPkB64 the recipient's identity encryption PUBLIC key
 * @returns the bundle entry { ownerUserId, memberUserId, encPk, signPk, sealed }
 */
export function sealInviteKey (sodium, inviteKey, idEncPkB64) {
  const secret = new TextEncoder().encode(JSON.stringify({ encSk: inviteKey.encSk, signSk: inviteKey.signSk }))
  return {
    ownerUserId: inviteKey.ownerUserId,
    memberUserId: inviteKey.memberUserId,
    encPk: inviteKey.encPk,
    signPk: inviteKey.signPk,
    sealed: b64(sodium, sealBox(sodium, secret, ub64(sodium, idEncPkB64), inviteKeyContext(inviteKey))),
  }
}

/** Open one bundle entry with the identity's X-Wing seed → { ...entry, encSk, signSk }. Throws when it is not sealed to this identity. */
export function openSealedInviteKey (sodium, entry, secrets) {
  const bytes = openBox(sodium, ub64(sodium, entry.sealed), ub64(sodium, secrets.id_enc_sk), inviteKeyContext(entry))
  const { encSk, signSk } = JSON.parse(new TextDecoder().decode(bytes))
  // The sealed half must belong to the published public half, or the entry
  // was tampered with between the claim and now.
  if (b64(sodium, epochPkFromSeed(sodium, ub64(sodium, encSk))) !== entry.encPk) {
    throw new Error('invite key secret does not match its published public key')
  }
  return { ...entry, encSk, signSk }
}

/**
 * Re-seal every entry to a NEW identity key (rotation): the old secrets open
 * them, the new public key seals them. Entries that fail to open are dropped
 * (they were useless to this identity anyway).
 */
export function reSealInviteKeys (sodium, entries, oldSecrets, newIdEncPkB64) {
  const out = []
  for (const entry of entries || []) {
    try {
      const opened = openSealedInviteKey(sodium, entry, oldSecrets)
      out.push(sealInviteKey(sodium, opened, newIdEncPkB64))
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('invite key dropped on rotation (could not be opened)', entry && entry.ownerUserId)
    }
  }
  return out
}

/** The bundle entry minted by `ownerUserId`, or null. */
export function findInviteKeyFor (entries, ownerUserId) {
  return (entries || []).find((k) => k && k.ownerUserId === ownerUserId) || null
}

/** Does a member-list entry name one of these invite keys (public halves)? */
export function memberEntryMatchesInviteKeys (memberEntry, entries, ownerUserId) {
  return (entries || []).some((k) => k && (!ownerUserId || k.ownerUserId === ownerUserId)
    && k.encPk === memberEntry.idEncPk && k.signPk === memberEntry.idSignPk)
}
